import type { AdminAgentIdentity, AdminKnowledgeDirectoryIdentity } from '../services/api';
import { buildAgentDetailPath, toPortalAgentType, type GotoTarget } from './portalLink';

/** Bezeichnung der Ziel-Art für die Erfolgsmeldung. */
export type GotoKindLabel = 'Agent' | 'Chat' | 'Knowledge';

/** Ergebnis der Ziel-Auflösung: wer wird impersoniert und wohin geht der Sprung. */
export interface ResolvedGotoTarget {
  /** Customer, für den die Impersonation gestartet wird. */
  customerId: string;
  /** Ziel-Pfad im Customer-Portal (führender Slash, ohne Host und Token). */
  path: string;
  /** Name des getroffenen Objekts — erscheint in der Erfolgsmeldung. */
  name: string;
  /** Bezeichnung der Ziel-Art für die Erfolgsmeldung. */
  kindLabel: GotoKindLabel;
  /**
   * Gespeicherte Schreibweise der getroffenen Rufnummer — nur beim Rufnummern-Weg gesetzt.
   * Wird im Erfolgs-Toast angezeigt, damit der Admin sieht, WELCHE Nummer getroffen wurde,
   * bevor er im Ziel-Tab weiterarbeitet.
   */
  matchedPhoneNumber?: string;
}

/** Ausschnitt des apiService, den die Goto-Auflösung braucht — injizierbar für Tests. */
export interface GotoLookupApi {
  getAgentByPhoneNumberAdmin(phoneNumber: string): Promise<AdminAgentIdentity>;
  getAgentByIdAdmin(agentId: string): Promise<AdminAgentIdentity>;
  getKnowledgeDirectoryByIdAdmin(directoryId: string): Promise<AdminKnowledgeDirectoryIdentity>;
}

/**
 * Ermittelt zu einem erkannten Ziel den besitzenden Customer und den finalen Pfad.
 * Je Ziel-Art greift ein anderer Admin-Lookup: Rufnummer und Agent-Link lösen über
 * den Agent auf, Knowledge-Links über das Verzeichnis. Link-Ziele reichen den
 * Eingabepfad unverändert durch; der Rufnummern-Weg baut den Pfad aus dem Lookup.
 * @param target Ergebnis aus {@link parseGotoInput}
 * @param api Admin-Lookup-Endpunkte (produktiv: `apiService`)
 * @returns Customer-ID, Ziel-Pfad und Beschriftungen für die Erfolgsmeldung
 */
export async function resolveGotoTarget(target: GotoTarget, api: GotoLookupApi): Promise<ResolvedGotoTarget> {
  switch (target.kind) {
    case 'phone-number': {
      const agent = await api.getAgentByPhoneNumberAdmin(target.phoneNumber);
      return {
        customerId: agent.customerId,
        path: buildAgentDetailPath(toPortalAgentType(agent.type), agent.id),
        name: agent.name,
        kindLabel: 'Agent',
        matchedPhoneNumber: agent.phoneNumber ?? target.phoneNumber,
      };
    }
    case 'agent-link': {
      const agent = await api.getAgentByIdAdmin(target.agentId);
      return {
        customerId: agent.customerId,
        path: target.path,
        name: agent.name,
        kindLabel: target.conversationId ? 'Chat' : 'Agent',
      };
    }
    case 'knowledge-link': {
      const directory = await api.getKnowledgeDirectoryByIdAdmin(target.knowledgeDirectoryId);
      return {
        customerId: directory.customerId,
        path: target.path,
        name: directory.name,
        kindLabel: 'Knowledge',
      };
    }
  }
}
